import { z } from 'zod';
import type { SubscriptionStore } from './api.js';

/**
 * The password bounds, in one place.
 *
 * Better Auth enforces these server-side from its own `emailAndPassword` config; these
 * constants are what that config is built from, and what every form validates against so
 * a password is never refused only after a round trip.
 */
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;

/** One signed-in session, as the account screen sees it. Deliberately carries no token. */
export interface AccountSessionDto {
  id: string;
  /** Derived from the stored user agent — "Chrome on macOS", "Platewell iOS". */
  label: string;
  /** The session making this request. Only the server can tell, so only the server says. */
  current: boolean;
  /**
   * `session.updatedAt`, which only advances once per `updateAge` (a day). Fine for
   * "last used", useless for "active now" — which is why the UI never claims the latter.
   */
  lastUsedAt: number;
  createdAt: number;
}

export interface AccountSecurityResponse {
  /** False for an account that only ever signed in with Google or Apple. */
  hasPassword: boolean;
  /** Linked providers, e.g. `['credential', 'google']`. */
  providers: string[];
  sessions: AccountSessionDto[];
}

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(MIN_PASSWORD_LENGTH).max(MAX_PASSWORD_LENGTH),
  revokeOtherSessions: z.boolean().optional(),
});

/**
 * Proof that the person at the keyboard means it.
 *
 * An account with a password sends that; one that only ever signed in with Google or
 * Apple has no password to send, so it types its email address instead.
 */
export const deleteAccountSchema = z.object({
  password: z.string().min(1).max(MAX_PASSWORD_LENGTH).optional(),
  confirmEmail: z.string().min(1).max(320).optional(),
});

export type DeleteAccountRequest = z.infer<typeof deleteAccountSchema>;

/** What deleting the account will do, for the confirmation screen to spell out. */
export interface AccountDeletionPreview {
  /** False for a social-only account, which confirms by typing its email instead. */
  requiresPassword: boolean;
  /** Households with no other member. Their recipes are destroyed with the account. */
  deletedHouseholds: { id: string; name: string }[];
  /** Shared households this user is the only owner of, and who becomes owner. */
  transferredHouseholds: { id: string; name: string; newOwnerName: string }[];
  /** Shared households that already have another owner. The user just leaves. */
  leftHouseholds: { id: string; name: string }[];
  /**
   * Renewing subscriptions this user bought. Deleting the account cannot cancel them;
   * `manageUrl` is the store's page for that, and null for a web subscription.
   */
  subscriptions: {
    householdId: string;
    householdName: string;
    store: SubscriptionStore;
    manageUrl: string | null;
  }[];
}
